import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/utils';

interface TooltipProps {
  children: React.ReactNode;
  content: string;
  side?: 'top' | 'bottom' | 'left' | 'right';
}

export function Tooltip({ children, content, side = 'bottom' }: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, transformX: 'translateX(-50%)', transformY: 'translateY(0)', arrowPosition: 'left-1/2 -translate-x-1/2' });
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const updatePosition = () => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top = 0;
    let left = 0;
    let transformX = 'translateX(-50%)';
    let transformY = 'translateY(0)';
    let arrowPosition = 'left-1/2 -translate-x-1/2';

    switch (side) {
      case 'bottom':
        top = triggerRect.bottom + scrollY + 8;
        // Center by default, but align to right if near viewport edge
        const tooltipWidth = tooltipRect.width || 80;
        const centeredLeft = triggerRect.left + scrollX + triggerRect.width / 2;
        
        if (centeredLeft + tooltipWidth / 2 > viewportWidth - 8) {
          // Align to right edge of button
          left = triggerRect.right + scrollX;
          transformX = 'translateX(-100%)';
          arrowPosition = 'right-4';
        } else if (centeredLeft - tooltipWidth / 2 < 8) {
          // Align to left edge of button
          left = triggerRect.left + scrollX;
          transformX = 'translateX(0)';
          arrowPosition = 'left-4';
        } else {
          // Center on button
          left = centeredLeft;
          transformX = 'translateX(-50%)';
          arrowPosition = 'left-1/2 -translate-x-1/2';
        }
        break;
      case 'top':
        top = triggerRect.top + scrollY - tooltipRect.height - 8;
        const tooltipWidthTop = tooltipRect.width || 80;
        const centeredLeftTop = triggerRect.left + scrollX + triggerRect.width / 2;
        
        if (centeredLeftTop + tooltipWidthTop / 2 > viewportWidth - 8) {
          left = triggerRect.right + scrollX;
          transformX = 'translateX(-100%)';
          arrowPosition = 'right-4';
        } else if (centeredLeftTop - tooltipWidthTop / 2 < 8) {
          left = triggerRect.left + scrollX;
          transformX = 'translateX(0)';
          arrowPosition = 'left-4';
        } else {
          left = centeredLeftTop;
          transformX = 'translateX(-50%)';
          arrowPosition = 'left-1/2 -translate-x-1/2';
        }
        break;
      case 'right':
        top = triggerRect.top + scrollY + triggerRect.height / 2;
        left = triggerRect.right + scrollX + 8;
        transformX = 'translateX(0)';
        transformY = 'translateY(-50%)';
        break;
      case 'left':
        top = triggerRect.top + scrollY + triggerRect.height / 2;
        left = triggerRect.left + scrollX - tooltipRect.width - 8;
        transformX = 'translateX(-100%)';
        transformY = 'translateY(-50%)';
        break;
    }

    // Final viewport bounds check
    if (left + tooltipRect.width > viewportWidth + scrollX) {
      left = viewportWidth + scrollX - tooltipRect.width - 8;
      transformX = 'translateX(0)';
      if (side === 'bottom' || side === 'top') {
        arrowPosition = 'right-4';
      }
    }
    if (left < scrollX) {
      left = scrollX + 8;
      transformX = 'translateX(0)';
      if (side === 'bottom' || side === 'top') {
        arrowPosition = 'left-4';
      }
    }
    if (top + tooltipRect.height > viewportHeight + scrollY) {
      top = triggerRect.top + scrollY - tooltipRect.height - 8;
    }
    if (top < scrollY) {
      top = scrollY + 8;
    }

    setPosition({ top, left, transformX, transformY, arrowPosition });
  };

  const handleMouseEnter = () => {
    setIsVisible(true);
    // Update position after a brief moment to ensure tooltip is rendered
    setTimeout(updatePosition, 0);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  useEffect(() => {
    if (isVisible) {
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isVisible]);

  const [arrowPosition, setArrowPosition] = useState('left-1/2 -translate-x-1/2');

  return (
    <>
      <div
        ref={triggerRef}
        className="inline-block"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>
      {isVisible &&
        createPortal(
          <div
            ref={tooltipRef}
            className={cn(
              'fixed z-[9999] px-2 py-1 text-xs font-medium text-white bg-gray-900 dark:bg-gray-800 rounded shadow-lg whitespace-nowrap pointer-events-none',
              'transform -translate-x-1/2 -translate-y-1/2'
            )}
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`,
              transform: `${position.transformX} ${position.transformY}`,
            }}
          >
            {content}
            <div
              className={cn(
                'absolute w-0 h-0 border-4 border-transparent',
                side === 'bottom' && 'bottom-full border-b-gray-900 dark:border-b-gray-800',
                side === 'top' && 'top-full border-t-gray-900 dark:border-t-gray-800',
                side === 'left' && 'left-full top-1/2 -translate-y-1/2 border-l-gray-900 dark:border-l-gray-800',
                side === 'right' && 'right-full top-1/2 -translate-y-1/2 border-r-gray-900 dark:border-r-gray-800',
                (side === 'bottom' || side === 'top') && position.arrowPosition
              )}
            />
          </div>,
          document.body
        )}
    </>
  );
}

